/**
 * Porter's Five Forces — the cross, and bands rather than numbers.
 *
 * THE SHAPE CARRIES THE MODEL. Five horizontal bars say "five independent
 * quantities", which is the one thing Porter's framework does not say: four
 * external forces bear on the rivalry in the middle, and that relationship is
 * the analysis. So rivalry sits at the centre with new entrants above,
 * substitutes below, and supplier and buyer power on the sides, connected to
 * it. The rejected implementation substituted bars and lost the model.
 *
 * NO RADAR EITHER. A radar's enclosed area reads as a total, and there is no
 * total here — the forces are not commensurable and are never summed.
 *
 * BANDS, NEVER NUMBERS. The methodology averages supported driver values to
 * pick a band. That mean never leaves the server, and this component could not
 * render it if it wanted to: `PorterBody` has no field for it. An average of
 * coarse judgements shown as `1.67` claims a precision its inputs do not have,
 * and readers anchor on the decimal instead of the drivers underneath it.
 *
 * THE CROSS AND THE LIST ARE THE SAME DATA. The cross is for a glance; the list
 * is the analysis and the accessible equivalent. Every force appears in both,
 * including the ones with insufficient evidence — a force that vanished because
 * nothing was found would read as a force that does not apply.
 *
 * Unknown drivers are LISTED. The methodology excludes them from the
 * calculation rather than scoring them neutral, and showing them is what makes
 * that difference visible.
 */

import React, { useState } from 'react';
import { Text, View } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SectionDivider } from '@/components/SectionDivider';
import { useAnalysisVisuals, type EvidenceTone } from './analysis-visuals';
import {
  AnalysisHero,
  DetailCard,
  Footnote,
  LabelledBlock,
  SectionHeading,
} from './AnalysisPrimitives';
import type { ClaimView, PorterBody, PorterForceName, PorterRating } from '@/lib/strategic-analysis/types';
import { DRIVER_LABELS, FORCE_LABELS, PRESSURE_LABELS, RATING_LABELS, driverLabel } from './framework-labels';
import { claimFor } from './claim-join';

const COUNTER_EVIDENCE_NOTES: Record<ClaimView['counterEvidenceStatus'], string> = {
  completed_with_results: 'Evidence pointing the other way was searched for and found.',
  none_found_after_search:
    'Evidence pointing the other way was searched for and none was found in that search. Not proof that none exists.',
  provider_failed: 'The search for evidence pointing the other way could not be completed.',
  filter_not_supported: 'That search ran more broadly than intended — a filter could not be applied.',
  budget_exhausted: 'The research budget was reached before that search could run.',
  not_required: 'No counter-evidence search was required for this driver.',
};

/**
 * A pressure band's colour.
 *
 * `insufficient_evidence` is GREY, never green. High pressure is adverse and
 * low pressure is favourable — but "we could not tell" is neither, and borrowing
 * the reassuring colour for it is the misread worth designing against.
 */
const RATING_TONE: Record<PorterRating, EvidenceTone> = {
  low: 'positive',
  moderate: 'caution',
  high: 'adverse',
  insufficient_evidence: 'unknown',
};

interface PorterFiveForcesProps {
  body: PorterBody;
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
}

export function PorterFiveForces({ body, claims, onInspectClaim }: PorterFiveForcesProps) {
  const v = useAnalysisVisuals();

  // Built from the fixed five, in cross order, so a force the research never
  // reached still occupies its position rather than collapsing the diagram.
  const forces = CROSS_ORDER.map((force) => {
    const found = body.forces.find((f) => f.force === force);
    return {
      force,
      rating: found?.rating ?? ('insufficient_evidence' as PorterRating),
      drivers: found?.drivers ?? [],
      supportedDriverCount: found?.supportedDriverCount ?? 0,
      unknownDrivers: found?.unknownDrivers ?? [],
    };
  });

  const [selected, setSelected] = useState<PorterForceName>('competitive_rivalry');
  const current = forces.find((f) => f.force === selected) ?? forces[0];

  return (
    <View className="gap-4">
      {/* The synthesis, as the server wrote it. There is no headline field and
          no overall score, so neither is manufactured to fill the slot. */}
      <AnalysisHero
        label="Overall attractiveness"
        body={body.overallSynthesis}
        accessibilityLabel={`Overall attractiveness. ${body.overallSynthesis}`}
      />
      <Footnote>
        A reading of the pattern across the five forces, not an average of them. There is no overall
        score.
      </Footnote>

      <SectionDivider label="Five forces" marginTop={4} marginBottom={0} />
      <View
        className="rounded-3xl p-3 gap-2"
        style={{
          backgroundColor: v.chart.surface,
          borderWidth: 1,
          borderColor: v.chart.border,
          overflow: 'hidden',
        }}
      >
        <View className="px-1">
          <SectionHeading title="How the five forces bear on rivalry" />
          <Text className="mt-0.5 text-[11px]" style={{ color: v.colors.text.tertiary }}>
            Select a force to inspect its evidence
          </Text>
        </View>

        <ForceCross forces={forces} selected={selected} onSelect={setSelected} />
      </View>

      {current && (
        <ForceDetail force={current} claims={claims} onInspectClaim={onInspectClaim} />
      )}

      {/* The complete equivalent. Every force, every band, always — and the same
          selection mechanism, so a reader who never touches the diagram loses
          nothing. */}
      <View
        accessibilityRole="summary"
        accessibilityLabel={`Five forces overview. ${forces
          .map((f) => `${FORCE_LABELS[f.force]}: ${RATING_LABELS[f.rating]}`)
          .join('. ')}`}
        className="gap-2"
      >
        <SectionDivider label="All five forces" marginTop={4} marginBottom={4} />
        {forces.map((force) => {
          const tone = v.tone(RATING_TONE[force.rating]);
          const isSelected = force.force === selected;
          return (
            <View
              key={`row-${force.force}`}
              className="rounded-2xl overflow-hidden"
              style={{
                minHeight: 52,
                backgroundColor: v.colors.surface.primary,
                borderWidth: 1,
                borderColor: isSelected ? v.colors.primary[600] : v.colors.border.light,
              }}
            >
              <AnimatedPressable
                onPress={() => setSelected(force.force)}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={`${FORCE_LABELS[force.force]}: ${RATING_LABELS[force.rating]}. ${force.supportedDriverCount} of ${force.drivers.length + force.unknownDrivers.length} drivers supported by evidence.`}
                className="flex-row items-center gap-2 px-3.5"
                style={{ flex: 1, minHeight: 52 }}
              >
                <View
                  className="rounded-full shrink-0"
                  style={{ width: 8, height: 8, backgroundColor: tone.fore }}
                />
                <Text numberOfLines={1} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
                  {FORCE_LABELS[force.force]}
                </Text>
                <Text numberOfLines={1} className="text-[13px] font-semibold shrink-0" style={{ color: tone.fore }}>
                  {RATING_LABELS[force.rating]}
                </Text>
              </AnimatedPressable>
            </View>
          );
        })}
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// The cross
// ---------------------------------------------------------------------------

/** Diagram order: top, left, centre, right, bottom. */
const CROSS_ORDER: PorterForceName[] = [
  'threat_of_new_entrants',
  'supplier_power',
  'competitive_rivalry',
  'buyer_power',
  'threat_of_substitutes',
];

type ResolvedForce = {
  force: PorterForceName;
  rating: PorterRating;
  drivers: PorterBody['forces'][number]['drivers'];
  supportedDriverCount: number;
  unknownDrivers: string[];
};

/**
 * Rivalry in the middle, the four external forces around it, connected.
 *
 * Laid out with flex rather than absolute coordinates: the same diagram has to
 * hold at 342pt and at 900pt, and a hand-computed layout that fits one clips the
 * other. The connectors are plain rules between the flex children, so they
 * cannot drift away from the tiles they join.
 */
function ForceCross({
  forces,
  selected,
  onSelect,
}: {
  forces: ResolvedForce[];
  selected: PorterForceName;
  onSelect: (force: PorterForceName) => void;
}) {
  const v = useAnalysisVisuals();
  const at = (force: PorterForceName) => forces.find((f) => f.force === force)!;

  // Every tile takes a SHARE of the row, and the connectors are fixed. Giving
  // the connectors flexGrow too let them win the space at 390pt and crushed the
  // side tiles to forty points wide — "Su p… / Bu y…".
  const spacer = <View style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0 }} />;

  return (
    // Capped and centred: left to fill a 1440pt window the four outer tiles
    // stretch until the connectors are longer than the diagram, and the cross
    // stops reading as one shape.
    <View className="items-center self-center" style={{ paddingVertical: 4, width: '100%', maxWidth: 720 }}>
      {/* Spacers put the top tile exactly over the centre one. */}
      <View className="flex-row items-center self-stretch">
        {spacer}
        <View style={{ width: CONNECTOR }} />
        <ForceTile force={at('threat_of_new_entrants')} selected={selected} onSelect={onSelect} centre />
        <View style={{ width: CONNECTOR }} />
        {spacer}
      </View>
      <Connector orientation="vertical" />

      <View className="flex-row items-center self-stretch">
        <ForceTile force={at('supplier_power')} selected={selected} onSelect={onSelect} fill />
        <Connector orientation="horizontal" />
        <ForceTile force={at('competitive_rivalry')} selected={selected} onSelect={onSelect} centre pivot />
        <Connector orientation="horizontal" />
        <ForceTile force={at('buyer_power')} selected={selected} onSelect={onSelect} fill />
      </View>

      <Connector orientation="vertical" />
      <View className="flex-row items-center self-stretch">
        {spacer}
        <View style={{ width: CONNECTOR }} />
        <ForceTile force={at('threat_of_substitutes')} selected={selected} onSelect={onSelect} centre />
        <View style={{ width: CONNECTOR }} />
        {spacer}
      </View>
      <View className="mt-1.5">
        <Text className="text-[10px]" style={{ color: v.colors.text.tertiary }}>
          Four external forces bear on the rivalry at the centre
        </Text>
      </View>
    </View>
  );
}

/** Fixed, so the tiles — not the rules between them — own the row's width. */
const CONNECTOR = 14;

function Connector({ orientation }: { orientation: 'vertical' | 'horizontal' }) {
  const v = useAnalysisVisuals();
  return orientation === 'vertical'
    ? <View style={{ width: 1.5, height: 18, backgroundColor: v.chart.axis }} />
    : <View style={{ height: 1.5, width: CONNECTOR, backgroundColor: v.chart.axis }} />;
}

function ForceTile({
  force,
  selected,
  onSelect,
  centre = false,
  pivot = false,
  fill = false,
}: {
  force: ResolvedForce;
  selected: PorterForceName;
  onSelect: (force: PorterForceName) => void;
  /** Occupies the centre column: top, middle and bottom tiles all align. */
  centre?: boolean;
  /** The middle tile itself — taller, because it is what the others bear on. */
  pivot?: boolean;
  fill?: boolean;
}) {
  const v = useAnalysisVisuals();
  const isSelected = force.force === selected;
  const tone = v.tone(RATING_TONE[force.rating]);

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{
        minHeight: pivot ? 78 : 64,
        // Proportional, never fixed: at 390pt three fixed tiles plus two
        // connectors do not fit, and at 1440pt they float apart.
        flexGrow: centre ? 1.35 : 1,
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 84,
        backgroundColor: isSelected ? v.colors.primary[50] : v.plate.background,
        borderWidth: isSelected ? 1.5 : 1,
        borderColor: isSelected ? v.colors.primary[600] : v.plate.border,
        ...v.plate.shadow,
      }}
    >
      <AnimatedPressable
        onPress={() => onSelect(force.force)}
        accessibilityRole="button"
        accessibilityState={{ selected: isSelected }}
        accessibilityLabel={`${FORCE_LABELS[force.force]}. Rated ${RATING_LABELS[force.rating]}.`}
        className="items-center justify-center gap-1.5 px-2 py-2"
        style={{ flex: 1, minHeight: pivot ? 78 : 64 }}
      >
        <Text
          numberOfLines={2}
          className={`text-center font-semibold ${pivot ? 'text-[13px]' : 'text-[12px]'}`}
          style={{ color: v.colors.text.primary }}
        >
          {FORCE_LABELS[force.force]}
        </Text>
        <View className="flex-row items-center justify-center gap-1.5">
          <View className="rounded-full shrink-0" style={{ width: 7, height: 7, backgroundColor: tone.fore }} />
          {/* Two lines, because "Insufficient evidence" does not fit one at
              tile width and is the last label that may ever be cut short. */}
          <Text
            numberOfLines={2}
            className="text-[10px] font-semibold"
            style={{ color: tone.fore, flexShrink: 1, textAlign: 'center' }}
          >
            {RATING_LABELS[force.rating]}
          </Text>
        </View>
      </AnimatedPressable>
    </View>
  );
}

// ---------------------------------------------------------------------------
// The selected force
// ---------------------------------------------------------------------------

function ForceDetail({
  force,
  claims,
  onInspectClaim,
}: {
  force: ResolvedForce;
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
}) {
  const v = useAnalysisVisuals();
  const tone = v.tone(RATING_TONE[force.rating]);
  const total = force.drivers.length + force.unknownDrivers.length;

  return (
    <DetailCard
      title={FORCE_LABELS[force.force]}
      badge={RATING_LABELS[force.rating]}
      badgeTone={tone}
      accessibilityLabel={`Selected force: ${FORCE_LABELS[force.force]}, ${RATING_LABELS[force.rating]}`}
    >
      <Text className="text-[11px]" style={{ color: v.colors.text.tertiary }}>
        {force.supportedDriverCount} of {total} drivers supported by evidence
      </Text>

      {force.drivers.length === 0 && force.unknownDrivers.length === 0 && (
        <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
          No driver for this force was accepted from the retained evidence. The band above reflects
          that absence rather than a finding about the market.
        </Text>
      )}

      {force.drivers.map((driver) => {
        // The key the assembler wrote, read verbatim. This used to rebuild
  // `${force}.${driver}` and compare it to a stored
  // `driver.${force}.${driver}` — the `driver.` prefix was missing, so the
  // lookup failed for every driver on every run and "View sources" never
  // rendered anywhere in production.
  const claim = claimFor(claims, driver.claimKey);
        return (
          <View
            key={`${force.force}-${driver.driver}`}
            className="rounded-2xl p-3 gap-2.5"
            style={{ backgroundColor: v.colors.surface.secondary, overflow: 'hidden' }}
          >
            <View className="flex-row items-center gap-2">
              <Text numberOfLines={2} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
                {driverLabel(driver.driver)}
              </Text>
              <Text
                numberOfLines={1}
                className="text-[11px] font-semibold shrink-0"
                style={{
                  color: driver.pressure === 2
                    ? v.colors.error
                    : driver.pressure === 0
                    ? v.colors.success
                    : v.colors.amber.main,
                }}
              >
                {PRESSURE_LABELS[driver.pressure]}
              </Text>
            </View>

            {/* Observation and interpretation are labelled separately and always
                in this order: what a source said, then what we read into it.
                Merging them would present an inference as an observation. */}
            <LabelledBlock label="Observed">{driver.observation}</LabelledBlock>
            <LabelledBlock label="AI interpretation">
              <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
                {driver.interpretation}
              </Text>
            </LabelledBlock>

            <LabelledBlock label="Counter-evidence search">
              <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
                {COUNTER_EVIDENCE_NOTES[driver.counterEvidenceStatus]}
              </Text>
            </LabelledBlock>

            {claim && onInspectClaim && (
              <Text
                accessibilityRole="button"
                accessibilityLabel={`View sources for ${driverLabel(driver.driver)}`}
                onPress={() => onInspectClaim(claim)}
                className="text-[13px] font-semibold"
                style={{ color: v.colors.primary[700] }}
              >
                View sources
              </Text>
            )}
          </View>
        );
      })}

      {force.unknownDrivers.length > 0 && (
        <View className="rounded-2xl p-3 gap-1" style={{ backgroundColor: v.colors.amber.light, overflow: 'hidden' }}>
          <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.amber.main }}>
            No evidence found
          </Text>
          {force.unknownDrivers.map((driver) => (
            <Text
              key={`${force.force}-unknown-${driver}`}
              className="text-[13px] leading-5"
              style={{ color: v.colors.text.primary }}
            >
              {DRIVER_LABELS[driver] ?? driver}
            </Text>
          ))}
          <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
            These are left out of the rating rather than counted as neutral.
          </Text>
        </View>
      )}
    </DetailCard>
  );
}
